/**
 * Help Content Registry
 *
 * Centralized content definitions for help dialogs across the application.
 * Use this file to define all help sections that can be displayed to users.
 *
 * Usage:
 * 1. Add a new section to HELP_CONTENT with a unique key
 * 2. Use useHelpDialog hook with that key to control the dialog
 * 3. Render HelpDialog component with the returned props
 */

// ============================================================================
// Types
// ============================================================================

/**
 * A single help item explaining a term or feature
 */
export interface HelpItem {
  /** The term, symbol, or feature name being explained */
  term: string;
  /** Clear description of what it means */
  description: string;
  /** Optional example to clarify usage */
  example?: string;
}

/**
 * A complete help section with multiple items
 */
export interface HelpSection {
  /** Unique identifier for this help section */
  id: string;
  /** Title displayed in the dialog header */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** List of help items to display */
  items: HelpItem[];
}

// ============================================================================
// Content Registry
// ============================================================================

/**
 * All help content sections available in the application.
 * Add new sections here as needed.
 */
export const HELP_CONTENT = {
  /**
   * Help for the Price Comparison Table component
   */
  priceComparison: {
    id: "price-comparison",
    title: "Ayuda: Comparativa de Precios",
    subtitle: "Entendé cómo leer la tabla de comparación",
    items: [
      {
        term: "Precio en verde",
        description:
          "Indica el mejor precio disponible para ese artículo entre todos los proveedores cotizados.",
      },
      {
        term: "+X%",
        description:
          "Muestra cuánto más caro es un precio respecto al mejor precio del mismo artículo.",
        example:
          "+22.3% significa que este proveedor cobra 22.3% más que la opción más barata.",
      },
      {
        term: "✓ Mejor precio",
        description:
          "Etiqueta que marca al proveedor con el precio más bajo para ese artículo.",
      },
      {
        term: "vs $ X.XXX (±Y%)",
        description:
          "Comparación con el precio de la última compra del mismo producto. Muestra si el precio actual es mayor (rojo) o menor (verde) que el histórico.",
        example:
          "vs $ 5.000 +10% significa que ahora cuesta 10% más que la última vez.",
      },
      {
        term: "Precio en azul (USD)",
        description:
          "Indica que el proveedor cotizó en dólares. Se muestra el equivalente en pesos según el tipo de cambio configurado.",
      },
      {
        term: "Mejor total",
        description:
          "El proveedor con el total más bajo sumando todos los artículos cotizados.",
      },
      {
        term: "Filtro de Proveedores",
        description:
          "Permite ocultar columnas de proveedores para comparar solo los que te interesan.",
      },
    ],
  },

  /**
   * Help for the Purchase Request Overview (main header section)
   */
  purchaseRequestOverview: {
    id: "purchase-request-overview",
    title: "Ayuda: Solicitud de Compra",
    subtitle: "Guía general sobre las secciones de esta pantalla",
    items: [
      {
        term: "¿Qué es una Solicitud de Compra?",
        description:
          "Es el pedido formal de materiales o servicios. Desde acá gestionás todo el proceso: desde recibir el pedido hasta aprobar la compra.",
      },
      {
        term: "Información General",
        description:
          "Datos básicos de la solicitud: número, fechas, sector/obra, y observaciones. Podés editar la mayoría de los campos haciendo clic en el ícono de lápiz.",
      },
      {
        term: "Solicitante y Comprador",
        description:
          "El solicitante es quien pidió los materiales. El comprador es quien gestiona las cotizaciones. Asignarlos correctamente es clave porque determina quién recibe notificaciones y puede aprobar.",
        example:
          "Si no asignás un comprador, nadie recibirá alertas de nuevas cotizaciones.",
      },
      {
        term: "Ítems de la Solicitud",
        description:
          "Lista de productos pedidos. Los ítems en amarillo no tienen producto asignado del catálogo; asignalos para poder comparar precios.",
      },
      {
        term: "Gestión de Invitaciones",
        description:
          "Acá invitás a proveedores a cotizar. Podés enviar por email, copiar el enlace para WhatsApp, o reenviar si no respondieron.",
      },
      {
        term: "Cotizaciones",
        description:
          "Subí PDFs de cotizaciones y la IA las procesa automáticamente, extrayendo precios y proveedores.",
      },
      {
        term: "Comparativa y Selección",
        description:
          "Compará precios entre proveedores y seleccioná qué comprar a quién. Esta sección aparece cuando tenés cotizaciones procesadas.",
      },
      {
        term: "Estados de la solicitud",
        description:
          "Pendiente → En proceso → Cotizado → Aprobado → Comprado. Cada estado indica en qué etapa está el pedido.",
      },
    ],
  },

  /**
   * Help for General Information section
   */
  generalInfo: {
    id: "general-info",
    title: "Ayuda: Información General",
    subtitle: "Cómo editar los datos de la solicitud",
    items: [
      {
        term: "Edición en línea",
        description:
          "Hacé clic en el ícono de lápiz junto a cualquier campo para editarlo. Presioná Enter para guardar o Esc para cancelar.",
      },
      {
        term: "Número de Solicitud",
        description:
          "Identificador único de esta solicitud. No se puede modificar.",
      },
      {
        term: "Sector (Obra)",
        description:
          "El proyecto u obra al que pertenece esta compra. Seleccionalo del listado para asignar correctamente el centro de costo.",
      },
      {
        term: "Fecha límite",
        description:
          "Fecha máxima para completar la compra. Si está próxima, la solicitud se resalta como urgente.",
      },
      {
        term: "Costo Estimado",
        description:
          "Presupuesto aproximado para esta compra. Útil para control y aprobaciones.",
      },
      {
        term: "Prioritario",
        description:
          "Marcá como prioritario para que aparezca destacado en los listados y reciba atención inmediata.",
      },
      {
        term: "Observaciones Generales",
        description:
          "Notas adicionales sobre la solicitud. Por ejemplo: condiciones especiales, urgencias, o instrucciones para el comprador.",
      },
      {
        term: "Ver documento origen",
        description:
          "Si la solicitud se creó desde un PDF o imagen, podés ver el documento original haciendo clic en este botón.",
      },
    ],
  },

  /**
   * Help for Request Items section (the grid with products)
   */
  requestItems: {
    id: "request-items",
    title: "Ayuda: Ítems de la Solicitud",
    subtitle: "Cómo gestionar los productos pedidos",
    items: [
      {
        term: "Filas en amarillo",
        description:
          "Indican ítems SIN producto asignado del catálogo. Hasta que no los asignes, no podrás ver precios históricos ni comparar cotizaciones correctamente.",
        example:
          "Hacé doble clic en la celda de Descripción para buscar y asignar el producto.",
      },
      {
        term: "Asignar producto",
        description:
          "Hacé doble clic en la columna Descripción para abrir el buscador. Escribí el nombre del producto y seleccionalo del catálogo.",
      },
      {
        term: "Columna 'Original'",
        description:
          "Muestra el texto original del pedido (antes de procesar). Aparece solo si hay diferencias con la descripción normalizada.",
      },
      {
        term: "Precio Promedio",
        description:
          "Precio estimado basado en cotizaciones anteriores. Si dice 'No cotizado', significa que aún no hay cotizaciones para este ítem.",
      },
      {
        term: "Última compra",
        description:
          "Muestra el precio, proveedor y fecha de la última vez que compraste este producto. Útil para comparar con cotizaciones nuevas.",
      },
      {
        term: "Ver historial",
        description:
          "Abre un panel con todo el historial de compras de ese producto: fechas, proveedores y precios anteriores.",
      },
      {
        term: "Agregar producto",
        description:
          "Usá el botón 'Agregar producto' para incluir ítems adicionales que no estaban en el pedido original.",
      },
      {
        term: "Exportar PDF",
        description:
          "Genera un PDF con la lista de ítems para compartir o archivar.",
      },
    ],
  },

  /**
   * Help for Invitations Management section
   */
  invitationsManagement: {
    id: "invitations-management",
    title: "Ayuda: Gestión de Invitaciones",
    subtitle: "Cómo invitar proveedores a cotizar",
    items: [
      {
        term: "¿Qué es una invitación?",
        description:
          "Es un enlace único que enviás a cada proveedor para que suba su cotización. El proveedor no necesita cuenta; solo el enlace.",
      },
      {
        term: "Estado: Pendiente",
        description:
          "La invitación se creó pero aún no se envió por email. Podés copiar el enlace y enviarlo manualmente.",
      },
      {
        term: "Estado: Enviado",
        description: "El email con la invitación ya fue enviado al proveedor.",
      },
      {
        term: "Estado: Visto",
        description:
          "El proveedor abrió el enlace de la invitación. Ya sabe que tiene que cotizar.",
      },
      {
        term: "Estado: Respondido",
        description:
          "El proveedor subió su cotización. Ya podés verla en la sección de cotizaciones.",
      },
      {
        term: "Mensaje + enlace",
        description:
          "Copia un mensaje pre-armado con instrucciones y el enlace. Ideal para pegar en WhatsApp o email.",
      },
      {
        term: "Solo enlace",
        description:
          "Copia únicamente la URL directa a la invitación, sin texto adicional.",
      },
      {
        term: "Enviar por WhatsApp",
        description:
          "Abre WhatsApp Web con el mensaje listo para enviar. Necesitás tener el teléfono del proveedor cargado.",
      },
      {
        term: "Reenviar",
        description:
          "Vuelve a enviar el email de invitación. Útil si el proveedor no lo recibió o fue a spam.",
      },
    ],
  },

  /**
   * Help for AI-generated Quotes section
   */
  aiQuotes: {
    id: "ai-quotes",
    title: "Ayuda: Cotizaciones por IA",
    subtitle: "Cómo funciona el procesamiento automático",
    items: [
      {
        term: "¿Cómo funciona?",
        description:
          "Subís un PDF o imagen de cotización y la IA extrae automáticamente: proveedor, precios, cantidades y condiciones.",
      },
      {
        term: "Estado: En espera",
        description: "El documento está en cola esperando ser procesado.",
      },
      {
        term: "Estado: Procesando",
        description:
          "La IA está analizando el documento. Puede tardar unos segundos.",
      },
      {
        term: "Estado: Procesado",
        description:
          "El documento se procesó correctamente. Ya tenés la cotización disponible.",
      },
      {
        term: "Estado: Error",
        description:
          "Hubo un problema al procesar. Intentá eliminar el documento y subirlo nuevamente.",
      },
      {
        term: "Indicador: Revisión",
        description:
          "La IA detectó posibles inconsistencias. Revisá la cotización manualmente para confirmar que los datos sean correctos.",
      },
      {
        term: "Indicador: Faltan ítems",
        description:
          "El proveedor no cotizó todos los productos de la solicitud. Muestra cuántos ítems quedaron sin precio.",
      },
      {
        term: "Indicador: Condiciones",
        description:
          "La cotización incluye aclaraciones especiales: plazo de entrega, forma de pago, validez, etc.",
      },
      {
        term: "Versiones anteriores",
        description:
          "Si un proveedor envió varias cotizaciones, se muestra la más reciente. Podés ver las anteriores desde el botón de historial.",
      },
      {
        term: "¿Documento mal procesado?",
        description:
          "Descargá el archivo original, eliminalo, y volvé a subirlo. La IA intentará procesarlo nuevamente.",
      },
    ],
  },

  /**
   * Help for Quote Management section (3 ways to load quotes)
   */
  quoteManagement: {
    id: "quote-management",
    title: "Ayuda: Gestión de Cotizaciones",
    subtitle: "Las 3 formas de cargar presupuestos a tu solicitud",
    items: [
      {
        term: "Enviar Solicitud",
        description:
          "Envía un email formal a los proveedores invitados solicitando su cotización. Cada proveedor recibe un enlace único para responder.",
      },
      {
        term: "Manual",
        description:
          "Ingresa los datos de la cotización manualmente. Útil si tenés los precios en papel, email, o formato no soportado por la IA.",
      },
      {
        term: "Cargar (Documentos)",
        description:
          "Subí PDFs o imágenes de cotizaciones y la IA las procesará automáticamente, extrayendo precios y proveedores.",
      },
      {
        term: "¿Cuál usar?",
        description:
          "Usá 'Enviar Solicitud' para cotizaciones formales. Usá 'Manual' para precios que ya tenés. Usá 'Cargar' para PDFs o fotos de cotizaciones.",
      },
      {
        term: "Formatos admitidos",
        description:
          "Para cargar documentos, aceptamos PDF y JPG/PNG. Asegurate que el archivo sea legible y que los precios estén claros.",
      },
      {
        term: "Múltiples documentos",
        description:
          "Podés subir varios documentos a la vez en el botón 'Cargar'. La IA los procesará uno por uno.",
      },
    ],
  },

  /**
   * Help for Comparativa y Selección section
   */
  comparisonAndSelection: {
    id: "comparison-and-selection",
    title: "Ayuda: Comparativa y Selección",
    subtitle: "Cómo usar las herramientas de comparación y selección de compra",
    items: [
      {
        term: "Comparativa de Precios",
        description:
          "Visualizá y compará los precios de todos los proveedores lado a lado. Podés ver quién tiene el mejor precio por artículo, qué porcentaje más caro está cada proveedor, y comparar con compras anteriores.",
        example:
          "Si un artículo está cotizado por 3 proveedores, el precio más bajo aparece en verde y los demás muestran +X% respecto al mejor.",
      },
      {
        term: "Selección de Compra",
        description:
          "Armá tu orden de compra eligiendo qué productos comprar a cada proveedor. Podés seleccionar el mejor precio de cada artículo o distribuir estratégicamente entre proveedores. El sistema te muestra automáticamente el resumen agrupado por proveedor.",
        example:
          "Podés comprar 5 artículos al Proveedor A (porque tiene mejor precio), 3 al Proveedor B (porque necesitás una marca específica), y combinar órdenes según tu estrategia.",
      },
      {
        term: "Filtrado de Proveedores",
        description:
          "En la comparativa, podés filtrar qué proveedores mostrar para facilitar la lectura cuando hay muchas cotizaciones. Esto es útil para enfocarte en los 2-3 mejores.",
      },
      {
        term: "Exportación de Órdenes",
        description:
          "Una vez que completaste la selección, podés exportar las órdenes de compra por proveedor en formato PDF o Excel. Cada proveedor recibe solo su orden específica.",
      },
      {
        term: "Guardado Automático",
        description:
          "Tus selecciones se guardan automáticamente a medida que las hacés. Podés volver más tarde y continuarán donde las dejaste.",
      },
      {
        term: "Mejor Total vs. Mejor Individual",
        description:
          "El proveedor con 'Mejor total' suma todos sus precios, pero puede no tener el mejor precio individual por artículo. Evaluá si te conviene comprar todo a uno (logística simple) o distribuir (mejores precios).",
      },
    ],
  },
  /**
   * Help for Purchase Selection Table component
   */
  purchaseSelection: {
    id: "purchase-selection",
    title: "Ayuda: Selección de Compra",
    subtitle: "Cómo seleccionar proveedores y guardar tu elección",
    items: [
      {
        term: "Selección gradual",
        description:
          "Podés seleccionar los materiales de a poco. No es necesario hacer todo de una vez. Guardá cuando quieras y seguí después.",
      },
      {
        term: "Barra de progreso",
        description:
          "Muestra cuántos materiales ya seleccionaste y cuántos faltan. Se actualiza automáticamente mientras hacés tu selección.",
      },
      {
        term: "Flechas de navegación",
        description:
          "Usá las flechas (< y >) a los costados de la tabla para desplazarte horizontalmente cuando hay muchos proveedores. Esto no oculta columnas, solo es navegación.",
      },
      {
        term: "Cómo seleccionar",
        description:
          "Hacé clic en la celda del proveedor que quieras para cada material. La celda se marcará en verde indicando la selección. Podés cambiar tu selección haciendo clic en otro proveedor.",
      },
      {
        term: "Guardado",
        description:
          "El botón 'Guardar selección' graba tus elecciones. Podés guardar tantas veces como quieras. Mientras la solicitud no entre en aprobación, podés modificar todo.",
      },
      {
        term: "Filtro de proveedores",
        description:
          "Usá el botón 'Proveedores' para ocultar columnas de proveedores que no te interesen. Esto facilita la comparación visual.",
      }
    ],
  },

  /**
   * Help for the Personal Hub / My Pending page
   */
  personalHub: {
    id: "personal-hub",
    title: "Ayuda: Hub Central",
    subtitle: "Tu espacio personal para gestionar tareas de todas tus organizaciones",
    items: [
      {
        term: "¿Qué es este espacio?",
        description:
          "Este es tu centro personal. Acá ves todas las solicitudes que esperan tu acción, sin importar a cuál organización pertenezcan. Si trabajás en varias empresas u obras, todo lo que necesitás revisar aparece junto en un solo lugar.",
      },
      {
        term: "Aprobaciones pendientes",
        description:
          "Son solicitudes de compra que necesitan tu firma o revisión. Las que están resaltadas en amarillo requieren tu acción inmediata — sos el próximo en la cadena de aprobación.",
      },
      {
        term: "Agrupación por organización",
        description:
          "Las solicitudes se agrupan por empresa/organización para que puedas identificar fácilmente de dónde viene cada una. Esto es especialmente útil si pertenecés a varias organizaciones.",
      },
      {
        term: "Mis organizaciones",
        description:
          "Podés ver todas las organizaciones a las que pertenecés. Desde acá podés navegar directamente a cualquiera de ellas.",
      },
    ],
  },
} as const satisfies Record<string, HelpSection>;

// ============================================================================
// Type Helpers
// ============================================================================

/**
 * Valid keys for help sections
 */
export type HelpSectionKey = keyof typeof HELP_CONTENT;

/**
 * Get a specific help section by key (type-safe)
 */
export function getHelpSection(key: HelpSectionKey): HelpSection {
  return HELP_CONTENT[key];
}
